import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Announcement {
  id: string;
  org_id: string;
  message: string;
  created_by_id: string | null;
  is_active: boolean;
  created_at: string;
  accepted_count?: number;
}

export interface AnnouncementAcceptance {
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  accepted_at: string;
}

export function useAnnouncements(orgId: string) {
  return useQuery({
    enabled: !!orgId,
    queryKey: ['announcements', orgId],
    queryFn: async () =>
      (await api.get<Announcement[]>(`/organizations/${orgId}/announcements`)).data,
  });
}

export function useAnnouncementAcceptances(orgId: string, annId: string | null) {
  return useQuery({
    enabled: !!orgId && !!annId,
    queryKey: ['announcements', orgId, annId, 'acceptances'],
    queryFn: async () =>
      (await api.get<AnnouncementAcceptance[]>(
        `/organizations/${orgId}/announcements/${annId}/acceptances`,
      )).data,
  });
}

export function useCreateAnnouncement(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { message: string }) => {
      const { data } = await api.post<Announcement>(
        `/organizations/${orgId}/announcements`,
        vars,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', orgId] }),
  });
}

export function useDeactivateAnnouncement(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      await api.delete(`/organizations/${orgId}/announcements/${vars.id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', orgId] }),
  });
}

export function useDismissAnnouncement(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      await api.post(`/organizations/${orgId}/announcements/${vars.id}/dismiss`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', orgId] }),
  });
}
